/**
 * The map's pure logic -- which colour an incident is, what the key says, and the
 * summary row under the map -- kept free of Leaflet/DOM so it can be unit tested
 * (test/web/incidentSummary.test.ts) and shared by the pins, the popups, the key and the
 * summary from one definition. main.ts does the drawing.
 */

/**
 * Four map-facing buckets, distinct from the domain's own status/cause:
 *  - planned:     scheduled works (cause planned) that are not yet finished;
 *  - new / in-progress: an unplanned outage, by whether the operator has given a
 *                 restoration estimate yet;
 *  - closed:      restored -- for the last hour only, per the API's own window
 *                 (src/db/queries.ts).
 * Deliberately simpler than the full domain model; this is what is useful at a glance.
 */
export type MarkerCategory = 'new' | 'in-progress' | 'planned' | 'closed';

export interface MapIncident {
  readonly status: string;
  readonly cause: string;
  readonly customersAffected: number | null;
  readonly startedAt: string | null;
  readonly estimatedRestoration: { readonly claim: { readonly kind: string } };
}

export function categorise(incident: Pick<MapIncident, 'status' | 'cause' | 'estimatedRestoration'>): MarkerCategory {
  if (incident.status === 'restored') return 'closed';
  if (incident.cause === 'planned') return 'planned';
  const kind = incident.estimatedRestoration.claim.kind;
  return kind === 'point' || kind === 'window' ? 'in-progress' : 'new';
}

/** One entry per colour: pin, popup badge and the map's key all read from here. */
export const MARKER_KEY: readonly { readonly category: MarkerCategory; readonly color: string; readonly label: string; readonly meaning: string }[] = [
  { category: 'new', color: '#FF3B30', label: 'New', meaning: 'Unplanned, no restoration estimate yet' },
  { category: 'in-progress', color: '#F59E0B', label: 'In progress', meaning: 'Unplanned, operator has given an estimate' },
  { category: 'planned', color: '#3BA9FF', label: 'Planned', meaning: 'Scheduled works' },
  { category: 'closed', color: '#10B981', label: 'Closed', meaning: 'Restored in the last hour' },
];

export const CATEGORY_COLOR = Object.fromEntries(MARKER_KEY.map((k) => [k.category, k.color])) as Record<MarkerCategory, string>;
export const CATEGORY_LABEL = Object.fromEntries(MARKER_KEY.map((k) => [k.category, k.label])) as Record<MarkerCategory, string>;

/**
 * Middle value of a sorted list of numbers -- unlike a mean, a single extreme
 * outlier (e.g. one incident an upstream feed never reconciled, sitting "active"
 * for years) can't drag this far from what most incidents actually look like.
 * Found live 2026-09-16: a handful of stale Electricity North West incidents
 * dating back to 2019 pulled the old mean-based figure up to 129+ days while the
 * true typical figure was a few hours -- see TODO.md.
 */
export function median(sortedAscending: readonly number[]): number {
  const mid = Math.floor(sortedAscending.length / 2);
  const upper = sortedAscending[mid] ?? 0;
  if (sortedAscending.length % 2 !== 0) return upper;
  const lower = sortedAscending[mid - 1] ?? upper;
  return (lower + upper) / 2;
}

export interface MapSummary {
  /** Unplanned incidents that are not closed. An unknown cause counts as an incident. */
  readonly incidents: number;
  /** Planned outages that are not finished (in progress or scheduled ahead). */
  readonly planned: number;
  /** Customers across the unplanned incidents that publish a count; null if none do. */
  readonly customers: number | null;
  /** Median minutes the unplanned incidents have been running; null if no start times. */
  readonly typicalDurationMinutes: number | null;
}

/**
 * The row under the map. Restored incidents are history, not "right now", so they are
 * excluded. Planned works are counted separately and deliberately kept OUT of the
 * incident volume, customer total and duration: a planned job is not an outage in the
 * sense a visitor asking "is there a power cut near me" means, and a long-running planned
 * job would drag the typical duration far from what unplanned incidents look like. Any
 * figure an operator doesn't publish (customersAffected, startedAt) is left out of its
 * total/typical-duration figure rather than treated as zero.
 */
export function summarise(incidents: readonly MapIncident[], now: number): MapSummary {
  const open = incidents.filter((i) => categorise(i) !== 'closed');
  const unplanned = open.filter((i) => i.cause !== 'planned');
  const planned = open.length - unplanned.length;

  const customerCounts = unplanned.map((i) => i.customersAffected).filter((n): n is number => n !== null);
  const durations = unplanned
    .map((i) => i.startedAt)
    .filter((s): s is string => s !== null)
    .map((s) => (now - new Date(s).getTime()) / 60_000)
    .filter((m) => m > 0)
    .sort((a, b) => a - b);

  return {
    incidents: unplanned.length,
    planned,
    customers: customerCounts.length > 0 ? customerCounts.reduce((sum, n) => sum + n, 0) : null,
    typicalDurationMinutes: durations.length > 0 ? median(durations) : null,
  };
}
